import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { IDV_OVERALL_STATUS, MAX_RETRIES } from 'constants/idv';
import {
  ALLOWED_TYPES_MODE_ID,
  ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ALLOWED_TYPES_MODE_STANDARD,
} from 'constants/upload-allowed-types';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import { selfieHelper } from 'helpers/candidate_application/pre-section-page-helpers';
import {
  extractFieldCodes,
  getBorderStatus,
  getTranslatedFailureMessage,
  getUnableToVerifyMessage,
  shouldAcceptAfterRetries,
  verifyIdDocument,
} from 'helpers/document-upload-helpers';
import { getUploadAcceptConfig } from 'helpers/upload-allowed-types';
import { useSubmitProgress } from 'hooks/use-submit-progress';
import { useVerifyIdDocumentByToken } from 'hooks/verify-id-document';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SelfieModal } from 'views/CandidateApp/CandidateForm/PreSubsectionFlows/components/Selfie/SelfieModal/SelfieModal';

import DisplayBox from 'components/DisplayBox/DisplayBox';
import { ExtractedFieldsDisplay } from 'components/ExtractedFieldsDisplay/ExtractedFieldsDisplay';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { SubmitProgressBar } from 'components/SubmitProgressBar/SubmitProgressBar';

import { getCandidateApplication } from 'api/candidate-application-slice';

import { DOCUMENT_UPLOAD_SLOT_STATE, DocumentUploadSlot } from './DocumentUploadSlot';

export const DocumentUpload = ({
  documentTitle = '',
  documentDescription = '',
  additionalInstructions = '',
  uploadCode,
  selfieId,
  reTakeSelfie,
  showTitleInUploadSlot = true,
  value,
  modelParams = {},
  listCode = '',
  metadata = {},
  className,
  bucketFolder,
  maxSize = 5242880,
  caption = '',
  accept,
  allowedTypesMode = ALLOWED_TYPES_MODE_STANDARD,
  idType,
  brand,
  useModel = false,
  disabled = false,
  hideDeleteButton = false,
  verifyDocument = false,
  rejected = false,
  expiryTolerance = null,
  skipVerification = false,
  needsComplementaryDoc = false,
  complementaryInstructions = '',
  displayFields = [],
  uploadError = '',
  uploadStatus = '',
  faceSimilarity = null,
  idValues = {},
  allowDisableFormActions = true,
  handleStateOnVerify = () => {},
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  deleteUpload = async () => true,
  uploadId = null,
  confirmedByCand = false,
}) => {
  const { token } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { verify: verifyByToken } = useVerifyIdDocumentByToken(token || undefined);
  const { disableFormActions, enableFormActions } = useDisableCandidateFormActions();

  const { effectiveAccept, allowedLabel, isFileAllowed } = getUploadAcceptConfig(
    allowedTypesMode,
    idType,
    accept,
    t,
  );

  const [verifying, setVerifying] = useState(false);
  const { progress: verifyProgress, status: verifyProgressStatus } = useSubmitProgress(verifying);
  const [status, setStatus] = useState(uploadStatus);
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(MessageType.Error);
  const [verificationError, setVerificationError] = useState(() => {
    if ((metadata?.attempts || 0) >= MAX_RETRIES) return null;
    if (
      uploadStatus === IDV_OVERALL_STATUS.FAILED ||
      uploadStatus === IDV_OVERALL_STATUS.REJECTED
    ) {
      return (
        getTranslatedFailureMessage(uploadError, null, faceSimilarity) || getUnableToVerifyMessage()
      );
    }
    return null;
  });
  const [isFileRejected, setIsFileRejected] = useState(rejected);
  const [isConfirmed, setIsConfirmed] = useState(confirmedByCand);

  useEffect(() => {
    if (confirmedByCand) {
      setIsConfirmed(true);
    }
  }, [confirmedByCand]);

  const getRetryCount = metadata?.attempts || 0;
  const isOnLastAttempt = getRetryCount >= MAX_RETRIES;

  useEffect(() => {
    if (uploadStatus != null && uploadStatus !== '') {
      setStatus(uploadStatus);
    }
  }, [uploadStatus]);

  useEffect(() => {
    if (verifying) return;
    if (getRetryCount >= MAX_RETRIES) {
      setVerificationError(null);
    }
    if (shouldAcceptAfterRetries(getRetryCount, uploadStatus || status)) {
      setVerificationError(null);
      setError(null);
      return;
    }
    // Don't let a lagging prop override a fresh local success
    const localIsSuccess =
      status === IDV_OVERALL_STATUS.VERIFIED ||
      status === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
      status === IDV_OVERALL_STATUS.NOT_APPLICABLE;
    if (localIsSuccess) {
      setVerificationError(null);
      return;
    }
    if (
      uploadStatus === IDV_OVERALL_STATUS.FAILED ||
      uploadStatus === IDV_OVERALL_STATUS.REJECTED
    ) {
      if (getRetryCount >= MAX_RETRIES) return;
      const message =
        getTranslatedFailureMessage(uploadError, null, faceSimilarity) ||
        getUnableToVerifyMessage();
      setVerificationError(message);
      return;
    }
    if (
      uploadStatus === IDV_OVERALL_STATUS.VERIFIED ||
      uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
      uploadStatus === IDV_OVERALL_STATUS.NOT_APPLICABLE
    ) {
      setVerificationError(null);
    }
  }, [uploadStatus, uploadError, faceSimilarity, verifying, getRetryCount, status]);

  // The slot owns the raw upload lifecycle (sign/S3/create, size/type validation, its own
  // "uploading"/"upload failed" visuals) — this component only reacts once a new upload has
  // actually landed, running verification on it. onUploadingChange/handledRef together
  // reproduce the original "disable form actions across the whole upload+verify window":
  // beginBusy fires the moment the slot starts uploading; endBusy fires either from
  // handleSlotChange's own finally (upload succeeded, verify — or the immediate onChange path
  // for non-verified documents — has now also finished) or, if the upload itself fails inside
  // the slot (handleSlotChange never runs), from onUploadingChange's own false callback.
  const handledRef = useRef(true);

  const beginBusy = useCallback(() => {
    handleStateOnVerify(true);
    if (allowDisableFormActions) {
      disableFormActions();
    }
  }, [handleStateOnVerify, allowDisableFormActions, disableFormActions]);

  const endBusy = useCallback(() => {
    handleStateOnVerify(false);
    if (allowDisableFormActions) {
      enableFormActions();
    }
  }, [handleStateOnVerify, allowDisableFormActions, enableFormActions]);

  const handleUploadingChange = useCallback(
    isUploading => {
      if (isUploading) {
        handledRef.current = false;
        beginBusy();
      } else if (!handledRef.current) {
        // Upload itself failed inside the slot — handleSlotChange never ran, so nothing else
        // will clear the busy/disabled state.
        endBusy();
      }
    },
    [beginBusy, endBusy],
  );

  const handleSlotChange = async newValue => {
    handledRef.current = true;

    if (newValue === null) {
      setStatus(null);
      setVerificationError(null);
      setError(null);
      setIsFileRejected(false);
      setIsConfirmed(false);
      onChange(null);
      return;
    }

    setIsConfirmed(false);
    setStatus(null); // clear stale status from a prior failed attempt
    setVerificationError(null);
    setError(null);

    const newUploadId = useModel ? newValue?.id : newValue;

    try {
      if (verifyDocument && listCode && token && newUploadId) {
        const updatedMetadata = {
          ...metadata,
          attempts: getRetryCount + 1,
        };
        const result = await verifyIdDocument(
          {
            uploadId: newUploadId,
            metadata: updatedMetadata,
            listCode,
            expiryTolerance,
            verifyByToken,
            reTakeSelfie,
            getUpload,
            useModel,
            value: newValue,
            skipVerification,
          },
          {
            setVerifying,
            setStatus,
            setError,
            setErrorType,
            setVerificationError,
            setShowSelfieModal,
            onChange: () => onChange(useModel ? newValue : newUploadId),
            setIsFileRejected,
          },
        );
        if (result.success) {
          setIsFileRejected(false);
        }
      } else {
        setIsFileRejected(false);
        onChange(useModel ? newValue : newUploadId);
      }
    } finally {
      endBusy();
    }
  };

  // Stamped for every upload, verified or not — IdRequirementsList uses this to scope which
  // list an upload belongs to, so the same id_type (e.g. a generic "driver_license" option
  // shared by two lists) doesn't render its verify/failed/confirm-details state in every list
  // that happens to offer a matching option. For verified documents this is also the ONLY
  // list-scope signal available until the verify call resolves and the backend populates
  // id_upload_detail.id_requirements_list — without it, the upload would briefly belong to no
  // list at all while "verifying...".
  const uploadMetadata = {
    ...metadata,
    attempts: getRetryCount + 1,
    ...(listCode ? { id_requirements_list: listCode } : {}),
  };

  // The slot's own deleteUpload call doesn't know about field codes — wrap it so a deletion
  // still clears any candidateValues entries tied to this document's extracted fields, exactly
  // as the original hand-rolled delete handler did.
  const deleteUploadWithFieldCodes = useCallback(
    id => {
      const codes = extractFieldCodes(displayFields);
      return deleteUpload(id, codes.length > 0 ? codes : null);
    },
    [deleteUpload, displayFields],
  );

  const uploadSelfie = useCallback(
    async photo => {
      await selfieHelper({ photo, candidate: { id: uploadCode }, signUpload, createUpload });
      if (selfieId) {
        await deleteUpload(selfieId);
      }
      if (token) {
        await dispatch(getCandidateApplication(token));
      }
      setShowSelfieModal(false);
    },
    [token, uploadCode, signUpload, createUpload, selfieId, deleteUpload, dispatch],
  );

  // Checked independently of `status` reaching NEEDS_REVIEW — that conversion only happens
  // when a LIVE verify call resolves at max attempts (validateRetryCount's setStatus callback);
  // on a plain reload, the persisted status can still read FAILED/REJECTED at max attempts, and
  // shouldAcceptAfterRetries already treats all three as force-accept-eligible. Without this,
  // the slot would never map to FORCE_ACCEPTED and Replace/Delete would stay live indefinitely.
  const isForceAccepted = shouldAcceptAfterRetries(getRetryCount, status);

  const borderStatus = getBorderStatus(status);
  const verificationState = isForceAccepted
    ? DOCUMENT_UPLOAD_SLOT_STATE.FORCE_ACCEPTED
    : borderStatus === 'u-border-error' && getRetryCount < MAX_RETRIES
      ? DOCUMENT_UPLOAD_SLOT_STATE.VERIFY_FAILED_RETRY
      : borderStatus === 'u-border-success'
        ? DOCUMENT_UPLOAD_SLOT_STATE.VERIFIED
        : undefined;

  const verification = verificationState
    ? {
        state: verificationState,
        errorMessage: verificationError,
        attemptCount: getRetryCount,
        maxAttempts: MAX_RETRIES,
      }
    : undefined;

  const showExtractedFields =
    displayFields?.length > 0 &&
    !!value &&
    !disabled &&
    (uploadStatus === IDV_OVERALL_STATUS.VERIFIED ||
      uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
      uploadStatus === IDV_OVERALL_STATUS.NOT_APPLICABLE ||
      status === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
      ((uploadStatus === IDV_OVERALL_STATUS.FAILED || status === IDV_OVERALL_STATUS.FAILED) &&
        isOnLastAttempt));

  return (
    <div
      className={cn('flex flex-column gap-4', { [className]: className, 'u-disabled': disabled })}>
      <SelfieModal
        show={showSelfieModal}
        brand={brand}
        screenName="camera"
        noClose
        title="Retake selfie"
        description="We ran into a little issue verifying your ID with the photo you provided. Could you take a clearer picture for us? Make sure your face is fully visible and well-lit."
        onSave={photo => uploadSelfie(photo)}
      />

      <div className="flex items-center justify-between">
        <DocumentUploadSlot
          heading={documentTitle}
          caption={caption}
          description={documentDescription}
          showTitle={showTitleInUploadSlot}
          additionalInstructions={additionalInstructions}
          brand={brand}
          disabled={disabled || verifying || isForceAccepted}
          hideDeleteButton={hideDeleteButton}
          rejected={isFileRejected}
          value={value}
          useModel={useModel}
          modelParams={modelParams}
          metadata={uploadMetadata}
          bucketFolder={bucketFolder}
          maxSize={maxSize}
          accept={effectiveAccept}
          acceptLabel={allowedLabel}
          isFileAllowed={isFileAllowed}
          getUpload={getUpload}
          signUpload={signUpload}
          createUpload={createUpload}
          deleteUpload={deleteUploadWithFieldCodes}
          onUploadingChange={handleUploadingChange}
          verification={verification}
          onChange={handleSlotChange}
          className="flex-1"
        />
      </div>

      {needsComplementaryDoc && complementaryInstructions && (
        <DisplayBox show={true} alignLeft>
          <span className="u-font-size-small u-font-weight-400">{complementaryInstructions}</span>
        </DisplayBox>
      )}

      {error && <FormMessage message={error} type={errorType} noMargin closeable={false} />}

      {showExtractedFields && (
        <ExtractedFieldsDisplay
          // Remount on a new upload, like DocumentUploadWithFields, so stale form state isn't reused.
          key={uploadId}
          displayFields={displayFields}
          idValues={idValues}
          brand={brand}
          uploadId={uploadId}
          documentTitle={documentTitle}
          confirmedByCand={isConfirmed}
          onConfirm={() => setIsConfirmed(true)}
        />
      )}

      <SubmitProgressBar
        progress={verifyProgress}
        status={verifyProgressStatus}
        loadingLabel={t('components.documentUploadSlot.verifying')}
      />
    </div>
  );
};

DocumentUpload.propTypes = {
  documentTitle: PropTypes.string,
  documentDescription: PropTypes.string,
  additionalInstructions: PropTypes.string,
  uploadCode: PropTypes.any,
  selfieId: PropTypes.number,
  reTakeSelfie: PropTypes.bool,
  value: PropTypes.any,
  modelParams: PropTypes.object,
  listCode: PropTypes.string,
  metadata: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  caption: PropTypes.string,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  allowedTypesMode: PropTypes.oneOf([
    ALLOWED_TYPES_MODE_STANDARD,
    ALLOWED_TYPES_MODE_ID,
    ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ]),
  idType: PropTypes.string,
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  disabled: PropTypes.bool,
  hideDeleteButton: PropTypes.bool,
  verifyDocument: PropTypes.bool,
  rejected: PropTypes.bool,
  expiryTolerance: PropTypes.number,
  skipVerification: PropTypes.bool,
  needsComplementaryDoc: PropTypes.bool,
  complementaryInstructions: PropTypes.string,
  displayFields: PropTypes.array,
  uploadError: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  uploadStatus: PropTypes.string,
  faceSimilarity: PropTypes.number,
  idValues: PropTypes.object,
  allowDisableFormActions: PropTypes.bool,
  handleStateOnVerify: PropTypes.func,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
  uploadId: PropTypes.number,
  confirmedByCand: PropTypes.bool,
};
